/**
 * English source strings, flat keys namespaced by surface (`nav.*`, `connect.*`, ...). Every key needs
 * an Arabic entry in `ar.ts`; plural keys use i18next suffixes (`_one`, `_other`; Arabic also needs
 * `_zero`, `_two`, `_few`, `_many`). Interpolations use `{{name}}`. Never translate identifiers, codes
 * or values that come from the cell.
 */
export const en = {
  "app.name": "Norn",
  "graph.title": "Object graph",
  "nav.graph": "Object graph",
  "graph.open": "Open in graph",
  "graph.search": "Find an object",
  "graph.closeSearch": "Close object search",
  "graph.searchHelp": "Choose a type and find an authorized object, or open an exact key in the graph.",
  "graph.searchType": "Object type",
  "graph.searchKey": "Key contains",
  "graph.searchPlaceholder": "Filter keys or enter an exact key…",
  "graph.searchEmpty": "No matching keys on this page.",
  "graph.searchPage": "Search applies to the current page.",
  "graph.openKey": "Open exact key",
  "graph.firstPage": "First page",

  "nav.expand": "Expand sidebar",
  "nav.collapse": "Collapse sidebar",
  "nav.controlPlane": "Control plane",
  "nav.installedApps": "Installed apps",
  "nav.primary": "Primary",
  "nav.overview": "Overview",
  "nav.definitions": "Definitions",
  "nav.data": "Data",
  "nav.explorer": "Explorer",
  "nav.objects": "Objects",
  "nav.ledger": "Ledger",
  "nav.apps": "Apps",
  "nav.operate": "Operate",
  "nav.inbox": "Inbox",
  "nav.findings": "Findings",
  "nav.permissions": "Permissions",
  "nav.menu": "Menu",
  "nav.closeMenu": "Close menu",

  "shell.userMenu": "Session details",
  "shell.notConnected": "Not connected",
  "shell.signedIn": "Signed in",
  "shell.signOut": "Sign out",
  "shell.disconnect": "Disconnect",
  "shell.themeLight": "Light theme",
  "shell.themeDark": "Dark theme",
  "shell.toggleTheme": "Toggle theme",
  "shell.actor": "actor {{actor}}",
  "shell.sessionEnded":
    "Your session ended and could not be renewed, so the cell refuses new requests. Signing in again reloads this page: copy anything unsaved first.",
  "shell.signInAgain": "Sign in again",

  "locale.label": "Language",
  "locale.en": "English",
  "locale.ar": "العربية",
  "locale.digits": "Digits",
  "locale.digitsLatn": "0123456789",
  "locale.digitsArab": "٠١٢٣٤٥٦٧٨٩",
  "locale.savedHere": "Saved for you on this device.",

  "connect.signInTitle": "Sign in",
  "connect.signInDescription": "Use your organization account. Norn checks what you may see and do on every request.",
  "connect.sessionExpired": "Your session ended and could not be renewed. Sign in again to continue.",
  "connect.signInButton": "Sign in with your organization",
  "connect.signInFailed": "The sign-in page could not be opened: {{error}}",
  "connect.callbackTitle": "Signing in…",
  "connect.callbackDescription": "Finishing sign-in with your organization.",
  "connect.callbackFailedTitle": "Sign-in did not complete",
  "connect.callbackFailedDescription": "The identity provider's answer could not be used. Nothing was saved.",
  "connect.tryAgain": "Try again",
  "connect.title": "Connect to a cell",
  "connect.description":
    "The console reads and changes one tenant cell through its HTTP API, as the identity your token carries.",
  "connect.cellUrl": "Cell URL",
  "connect.invalidUrl": "That isn't a valid URL.",
  "connect.otherCellToken": "That token belongs to a different cell. Paste the token for this cell.",
  "connect.token": "Bearer token",
  "connect.tokenPlaceholder": "Paste an access token",
  "connect.tokenCleared": "The previous cell's token was cleared because the URL points at a different cell. ",
  "connect.tokenHelp": "Kept in this tab only and cleared when it closes. Never sent anywhere but this cell.",
  "connect.devActor": "Development actor",
  "connect.devActorHelp": "Sent as {{header}}. Only a cell in explicit development mode honours it.",
  "connect.submit": "Connect",

  "scope.chooseTitle": "Choose a scope",
  "scope.chooseDescription":
    "Every read and change names its organization and environment explicitly. Connected to {{cell}}.",
  "connect.signingIn": "Signing in",
  "connect.tokenChangedHelp":
    "The previous cell's token was cleared because the URL points at a different cell. Kept in this tab only and cleared when it closes. Never sent anywhere but this cell.",
  "scope.wrongCell": "Wrong cell?",
  "scope.changeConnection": "Change connection",
  "scope.aCell": "a cell",
  "scope.organization": "Organization",
  "scope.environment": "Environment",
  "scope.open": "Open scope",
  "scope.recent": "Recent",
  "scope.switch": "Switch scope",
  "scope.changeLabel": "Scope: {{orgId}} / {{environmentId}}. Change scope",
  "shell.breadcrumb": "Breadcrumb",
  "copy.copied": "Copied",
  "copy.label": "Copy",
  "copy.value": "Copy {{value}}",
  "states.loading": "Loading",
  "states.credentialsRefused": "This cell did not accept your credentials",
  "states.updateConnection": "Update connection",
  "states.unreachableHelp":
    "The request never got a response. Check that the cell is running, the URL is right, and this console's origin is in the cell's allowed origins.",
  "states.cannotRead": "You can't read {{what}} in this scope",
  "states.cannotReach": "Can't reach {{url}}",
  "states.cannotLoad": "Couldn't load {{what}}",

  "definitions.kind.entity": "Entities",
  "definitions.kind.action": "Actions",
  "definitions.kind.rule": "Decisions",
  "definitions.kind.mapping": "Mappings",
  "definitions.kind.sourceSchema": "Source schemas",
  "definitions.kind.connector": "Connectors",
  "definitions.kind.policy": "Policies",
  "definitions.kind.widget": "Widgets",

  "connect.devActorFormattedHelp":
    "Sent as <header>{{header}}</header>. Only a cell in explicit development mode honours it.",

  "common.refresh": "Refresh",
  "common.cancel": "Cancel",
  "common.working": "Working…",
  "common.previous": "Previous",
  "common.next": "Next",
  "common.page": "Page {{page}}",
  "inbox.acknowledge": "Acknowledge",
  "inbox.acknowledgeConfirm": "Close without resending",
  "inbox.acknowledgeConsequence":
    "Closes the task. The command is never sent again; reconcile the target system by hand if needed.",
  "inbox.confirmAcknowledge": "Confirm acknowledge",
  "inbox.release": "Release",
  "inbox.releaseConfirm": "Release for one retry",
  "inbox.releaseConsequence": "You assert the command did not apply in the target system. Norn may retry it once.",
  "inbox.confirmRelease": "Confirm release",
  "inbox.unattributedCommand": "unattributed command",
  "inbox.needsReconciliation": "needs reconciliation",
  "inbox.retryUsed": "retry used",
  "inbox.status.failed": "failed",
  "inbox.status.dispatching": "dispatching",
  "inbox.status.retrying": "retrying",
  "inbox.status.resolved": "resolved",
  "inbox.status.acknowledged": "acknowledged",
  "inbox.status.released": "released",
  "inbox.resolvedBy": "by {{name}}",
  "inbox.recoveryRetryUsed": "This task already used its one recovery retry",
  "inbox.title": "Failed commands",
  "inbox.description":
    "Outbound commands that failed terminally. Decide each one: acknowledge to close it, or release to assert it never applied and allow one retry.",
  "inbox.taskState": "Task state",
  "inbox.needsDecision": "Needs a decision",
  "inbox.resolvedInFlight": "Resolved and in flight",
  "inbox.page": "page {{page}}",
  "inbox.commandTasks": "command tasks",
  "inbox.nothingNeedsDecision": "Nothing needs a decision",
  "inbox.nothingNeedsDecisionPage": "Nothing needs a decision on this page",
  "inbox.nothingResolved": "Nothing resolved or in flight",
  "inbox.nothingResolvedPage": "Nothing resolved or in flight on this page",
  "inbox.moreTasks": "More tasks continue on the next page.",
  "inbox.allResolved": "Every outbound command either succeeded or was already resolved.",
  "inbox.resolvedDescription":
    "Acknowledged and released tasks appear here with who resolved them, alongside commands still dispatching or retrying.",
  "inbox.pageCounts_one": "Counts are for this page of up to {{count}} task.",
  "inbox.pageCounts_other": "Counts are for this page of up to {{count}} tasks.",
  "inbox.humanApprovalsDeferred": "Human approvals and work tasks are not unified here yet (#3071).",
  "findings.classification.integrity": "Integrity",
  "findings.classification.integrityHint":
    "An integrity constraint: commands that would break it are refused; observed source data that breaks it is recorded as a finding.",
  "findings.classification.quality": "Quality",
  "findings.classification.qualityHint":
    "A quality expectation: it never refuses a command; every violation is recorded as a finding.",
  "findings.classification.builtin": "Built-in check",
  "findings.classification.builtinHint":
    "A rule Norn applies itself, such as temporal validity, lifecycle paths or identity isolation.",
  "findings.classification.unverified": "Unverified",
  "findings.classification.unverifiedHint":
    "The cell could not verify the rule behind this finding, so its classification is unknown.",
  "findings.ruleBuiltIn": "built-in · {{id}}",
  "findings.unpinnedVersion": "unpinned (legacy record)",
  "findings.outcome.notSatisfied": "The expression evaluated to false.",
  "findings.outcome.nonBoolean": "The expression returned {{actual}}, not a boolean.",
  "findings.outcome.evaluationFailed": "The expression could not be evaluated: {{reason}}",
  "findings.outcome.noPath": "Observed {{property}} = {{state}} with no declared lifecycle path.",
  "findings.outcome.noPathFrom": "Observed {{property}} = {{state}} from {{from}} with no declared lifecycle path.",
  "findings.outcome.multiplePaths": "Observed {{property}} = {{state}} with more than one shortest declared path.",
  "findings.outcome.multiplePathsFrom":
    "Observed {{property}} = {{state}} from {{from}} with more than one shortest declared path.",
  "findings.outcome.invalidTemporal": "{{property}} = {{value}} is not a valid temporal value: {{reason}}",
  "findings.outcome.identityIsolation": "Identity row isolated ({{code}}): {{detail}}",
  "findings.lastSeenDate": "last seen {{date}}",
  "findings.lastSeenNow": "last seen just now",
  "findings.lastSeenMinutes_one": "last seen {{count}}m ago",
  "findings.lastSeenMinutes_other": "last seen {{count}}m ago",
  "findings.lastSeenHours_one": "last seen {{count}}h ago",
  "findings.lastSeenHours_other": "last seen {{count}}h ago",
  "findings.lastSeenDays_one": "last seen {{count}}d ago",
  "findings.lastSeenDays_other": "last seen {{count}}d ago",
  "findings.any": "Any",
  "findings.firstSeen": "first seen {{date}}",
  "findings.hideEvidence": "Hide evidence",
  "findings.evidence": "Evidence",
  "findings.provenance":
    "Source <sourceValue>{{source}}</sourceValue> mapped by <mapping>{{mapping}}</mapping>, recorded {{recordedAt}}",
  "findings.provenanceObservations":
    "Source <sourceValue>{{source}}</sourceValue> mapped by <mapping>{{mapping}}</mapping>, recorded {{recordedAt}} · observation {{observations}}",
  "findings.noLineage": "No source lineage is recorded for this finding.",
  "findings.title": "Data-quality findings",
  "findings.description":
    "Evidence that data broke a declared rule or a built-in check. <integrity>Integrity</integrity> constraints refuse commands, so their findings come from observed source data; <quality>quality</quality> expectations never refuse and always record. Findings for objects you can't read are concealed.",
  "findings.filterStatus": "Status",
  "findings.filterSeverity": "Severity",
  "findings.ruleId": "Rule id",
  "findings.objectType": "Object type",
  "findings.subjectObjectType": "Subject object type",
  "findings.objectKey": "Object key",
  "findings.subjectKey": "Subject key",
  "findings.dataQualityFindings": "data-quality findings",
  "findings.noMatches": "No findings match these filters",
  "findings.noneVisible": "No findings you can see",
  "findings.clearFilter": "Clear a filter to widen the search.",
  "findings.noFindingsDescription":
    "No visible data broke a declared rule or a built-in check. Findings about objects outside your read access are concealed rather than counted.",

  "common.none": "None",
  "common.all": "All",
  "common.tenant": "Tenant",
  "common.registry": "Registry",
  "common.noneDeclared": "None declared.",
  "overview.title": "Installed definition set",
  "overview.description":
    "What this cell has admitted for {{orgId}} / {{environmentId}}, as its checker receipt attests.",
  "overview.cell": "Cell",
  "overview.setVersion": "Set version",
  "overview.receipt": "Receipt",
  "overview.registryBase": "Registry base",
  "overview.noneSelected": "None selected",
  "overview.unreadableEntries": "Unreadable entries",
  "overview.unreadable_one":
    "{{count}} served entry is not artifact envelopes and are not shown. The cell and this console may disagree on the contract version.",
  "overview.unreadable_other":
    "{{count}} served entries are not artifact envelopes and are not shown. The cell and this console may disagree on the contract version.",
  "overview.checkerFindings": "Checker findings",
  "overview.nonError_one": "{{count}} non-error",
  "overview.nonError_other": "{{count}} non-error",
  "overview.byKind": "By kind",
  "overview.kind": "Kind",
  "overview.total": "Total",
  "definitions.what": "definitions",
  "definitions.emptyTitle": "No definitions installed",
  "definitions.emptyDescription":
    "This scope has an empty admitted set. Definitions arrive through a checked install (<route>PUT /v1/defs</route>) or a registry release selected at bootstrap.",
  "definitions.layer": "Layer",
  "definitions.registryReadOnly": "Registry base, read-only",
  "definitions.setVersion": "Installed definition set version",
  "definitions.installedVersion": "set v{{version}}",
  "definitions.refresh": "Refresh definitions",
  "definitions.filter": "Filter {{kind}}",
  "definitions.emptyKindTitle": "No {{kind}} in this scope",
  "definitions.emptyKindDescription":
    "Nothing of kind <kind>{{kind}}</kind> is admitted for {{orgId}} / {{environmentId}}.",
  "definitions.noMatches": "Nothing matches that filter.",
  "definitions.select": "Select one of the {{kind}} to read its canonical definition.",
  "definitionDetail.what": "this definition",
  "definitionDetail.notInstalledTitle": "Not in the installed set",
  "definitionDetail.notInstalledDescription":
    "<artifact>{{artifactId}}</artifact> is not an admitted {{kind}} artifact at set v{{version}}. It may have been removed by a later install.",
  "definitionDetail.editDecisionTable": "Edit decision table",
  "definitionDetail.namespace": "namespace {{namespace}}",
  "definitionDetail.registryRelease":
    "Registry base, read-only. It comes from <release>{{release}}</release>; change it by selecting a different release, not by editing it here.",
  "definitionDetail.registryReleases":
    "Registry base, read-only. It comes from the selected registry releases; change it by selecting a different release, not by editing it here.",
  "definitionDetail.summary": "Summary",
  "definitionDetail.canonicalJson": "Canonical JSON",
  "definitionDetail.referencedBy": "Referenced by",
  "definitionDetail.noReferences": "No other installed definition names this one.",
  "definitionSummary.sharedPropertyTypes": "Shared property types",
  "definitionSummary.name": "Name",
  "definitionSummary.type": "Type",
  "definitionSummary.description": "Description",
  "definitionSummary.extends": "extends",
  "definitionSummary.implements": "implements",
  "definitionSummary.properties": "Properties",
  "definitionSummary.property": "Property",
  "definitionSummary.constraints": "Constraints",
  "definitionSummary.value": "Value",
  "definitionSummary.computedReadOnly": "computed · read-only",
  "definitionSummary.default": "default",
  "definitionSummary.links": "Links",
  "definitionSummary.link": "Link",
  "definitionSummary.target": "Target",
  "definitionSummary.cardinality": "Cardinality",
  "definitionSummary.inverse": "Inverse",
  "definitionSummary.required": " · required",
  "definitionSummary.keys": "Keys",
  "definitionSummary.key": "Key",
  "definitionSummary.lifecycle": "Lifecycle · {{property}}",
  "definitionSummary.transition": "Transition",
  "definitionSummary.from": "From",
  "definitionSummary.to": "To",
  "definitionSummary.legacyDecision":
    "RuleIR {{version}} decision. Its table or literal is in the canonical JSON; this summary covers 2.0 decision graphs.",
  "definitionSummary.rule": "Rule",
  "definitionSummary.form": "Form",
  "definitionSummary.ruleForm": "RuleIR {{version}} · contract <contract>{{contract}}</contract>",
  "definitionSummary.triggers": "Triggers",
  "definitionSummary.on": "On",
  "definitionSummary.detail": "Detail",
  "definitionSummary.slice": "Slice",
  "definitionSummary.decisionGraph": "Decision graph",
  "definitionSummary.node": "Node",
  "definitionSummary.content": "Content",
  "definitionSummary.tableNode_one": "{{count}} rows · {{inputCount}} in · {{outputCount}} out · hit {{hitPolicy}}",
  "definitionSummary.tableNode_other": "{{count}} rows · {{inputCount}} in · {{outputCount}} out · hit {{hitPolicy}}",
  "definitionSummary.expressionNode_one": "{{count}} expressions",
  "definitionSummary.expressionNode_other": "{{count}} expressions",
  "definitionSummary.input": "Input",
  "definitionSummary.field": "Field",
  "definitionSummary.preconditions": "Preconditions",
  "definitionSummary.effect": "Effect",
  "definitionSummary.transitionEffect":
    " fires <transition>{{transition}}</transition> on <object>{{object}}</object>.{{lifecycle}}",
  "definitionSummary.commands": "Commands",
  "definitionSummary.connector": "Connector",
  "definitionSummary.operation": "Operation",
  "definitionSummary.writes": "Writes",
  "definitionSummary.change": "Change",
  "definitionSummary.mappingDescription":
    "Declared from source schema <schema>{{schema}}</schema> <version>{{version}}</version>. These are the declared expressions, not recorded lineage.",
  "definitionSummary.expression": "Expression",
  "definitionSummary.stateProperty": "{{property}} (state)",
  "definitionSummary.source": "<system>{{system}}</system> {{version}} at <instance>{{instance}}</instance>.",
  "definitionSummary.sourceObserved":
    "<system>{{system}}</system> {{version}} at <instance>{{instance}}</instance>, observed {{observedAt}}.",
  "definitionSummary.fields": "Fields",
  "definitionSummary.dataset": "Dataset",
  "definitionSummary.nativeType": "Native type",
  "definitionSummary.candidate": "Candidate",
  "definitionSummary.roleCondition": "role ∈ {{roles}}",
  "definitionSummary.relationshipCondition": "{{from}} {{role}} principal",
  "definitionSummary.scopeMatch": "same {{dimensions}}",
  "definitionSummary.scopeUnspecified": "scope unspecified",
  "definitionSummary.conjunction": "{{left}} and {{right}}",
  "definitionSummary.scopeOnly": "{{scope}} (scope only)",
  "definitionSummary.policyDescription":
    "AccessIR rules as declared. Norn compiles these to Cedar; whether a given person may act also depends on capabilities, scope and delegation.",
  "definitionSummary.rules": "Rules",
  "definitionSummary.resource": "Resource",
  "definitionSummary.when": "When",
  "definitionSummary.noScalarFields": "No scalar fields.",
  "definitionSummary.structuredFields": "Structured fields ({{fields}}) are in the canonical JSON.",
  "lifecycle.diagram_one": "Lifecycle diagram, {{count}} states",
  "lifecycle.diagram_other": "Lifecycle diagram, {{count}} states",

  "explorer.types": "Types",
  "explorer.lineage": "Lineage",
  "explorer.layingOutGraph": "Laying out graph",
  "explorer.noObjectTypes": "No object types installed",
  "explorer.typeGraphDescription":
    "The type graph draws object types, interfaces and their links once entity definitions are installed.",
  "explorer.showingRegistryBase": "Showing registry base",
  "explorer.includeRegistryBase": "Include registry base",
  "explorer.details": "{{name}} details",
  "explorer.registryBase": "registry base",
  "explorer.tenant": "tenant",
  "explorer.closeDetails": "Close details",
  "explorer.openDefinition": "Open definition",
  "explorer.browseObjects": "Browse objects",
  "explorer.noMappings": "No mappings installed",
  "explorer.lineageDescription":
    "Declared lineage draws source datasets through MappingIR into object types. Install a mapping and its source schema to see it here.",
  "explorer.source": "Source",
  "explorer.mappingDetails": "Mapping details",
  "explorer.declaredExpressions":
    "Declared expressions. For what actually produced a value, open an object and read its recorded why.",
  "explorer.lifecycle": "lifecycle",
  "explorer.openMappingDefinition": "Open mapping definition",
  "explorer.browseType": "Browse {{name}}",
  "graph.interface": "interface",
  "graph.objectType": "object type",
  "graph.addedByExtension": "Added by the {{name}} extension",
  "graph.declaredByDefinitions": "Declared by definitions",
  "graph.recordedInLedger": "Recorded in the ledger",
  "graph.noOwnProperties": "No own properties",
  "graph.sourceDataset": "{{system}} · source dataset",
  "graph.namespacedObjectType": "{{namespace}} · object type",
  "graph.unmapped": "unmapped",
  "objects.commandFailuresDescription": "Command failures that need human follow-up.",
  "objects.description":
    "Committed objects by type, as this identity may read them. Pages are bounded; nothing loads a whole instance graph.",
  "objects.objectTypes": "object types",
  "objects.registryBaseType": "Registry base type",
  "objects.system": "system",
  "objects.typeObjects": "{{name}} objects",
  "objects.noReadableObjects": "No {{name}} objects you can read",
  "objects.emptyDescription":
    "Either none are committed in {{orgId}} / {{environmentId}}, or none are visible to this identity. Objects arrive through actions, rules and source observations.",
  "objects.version": "Version",
  "objects.deletedInSource": "deleted in source",
  "objects.pageSummary": "Page {{page}} · up to {{limit}} per page",
  "objects.readAs": "read as {{name}}",
  "objects.why": "Why",
  "objects.values": "Values",
  "objects.json": "JSON",
  "objects.evidenceReadFailed": "Recorded evidence could not be read, so no field shows where it came from.",
  "objects.evidenceVersionMismatch":
    "Recorded evidence was read for v{{evidenceVersion}}; this object is v{{objectVersion}}. Evidence is hidden until both are read at the same version.",
  "objects.readBothAgain": "Read both again",
  "objects.partialHistory":
    "Showing the recent history this read covers; older steps sit outside the read budget and can be opened in Why.",
  "objects.noRecordedSource": "No recorded source",
  "objects.historyHidden": "History hidden by policy",
  "objects.via": "via",
  "objects.recordedAndObservedAt": "recorded {{recordedAt}}, observed {{observedAt}}",
  "objects.recordedAt": "recorded {{recordedAt}}",
  "objects.evidenceCommit": "· commit {{commit}} · v{{version}}",
  "objects.history": "this object's history",
  "objects.noRecordedHistory": "No recorded history visible",
  "objects.noRecordedHistoryDescription": "The cell recorded no causes for this version that this identity may read.",
  "objects.recordedSteps": "Recorded steps",
  "objects.steps": "Steps",
  "objects.historyTruncated": "Earlier history not shown: these are the recent steps this read covers.",
  "objects.openInbox": "Open inbox",
  "objects.stepDecision": "Decision {{name}}",
  "objects.stepAction": "Action {{name}}",
  "objects.stepObserved": "Observed {{name}}",
  "objects.stepTransitionStates": "Transition {{name}} · {{from}} → {{to}}",
  "objects.stepTransition": "Transition {{name}}",
  "objects.stepTimerScheduledFired": "Timer {{name}} scheduled, fired",
  "objects.stepTimerScheduled": "Timer {{name}} scheduled",
  "objects.stepTimerFired": "Timer {{name}} fired",
  "objects.stepCommandEnqueued": "Command {{name}} enqueued",
  "objects.stepCommandResult": "Command result {{name}}",
  "objects.matchedRow": "row {{row}}",
  "objects.openObject": "Open object",
  "neighborhood.loadingLinks": "Loading links",
  "neighborhood.moreLinksMayExist": "More links may exist",
  "neighborhood.notExpanded": "Not expanded",
  "neighborhood.committedLinks": "Committed links, as you may read them",
  "neighborhood.selectedObject": "Selected object",
  "neighborhood.showLinks": "Show links",
  "neighborhood.retry": "Retry",
  "neighborhood.loadMoreLinks": "Load more links",
  "neighborhood.moreLinksDescription":
    "More links may exist. A page can be short or empty while the cell still has more to scan.",
  "neighborhood.scanLimit":
    "The cell hit its scan budget for this object's links. Retry continues from where it stopped.",
  "ledger.eventFallback": "event",
  "ledger.verifiedHeadHash": "Verified head hash {{hash}}",
  "ledger.head": "head",
  "ledger.theLedger": "the ledger",
  "ledger.empty": "The ledger is empty",
  "ledger.nothingAfterPosition": "Nothing after this position",
  "ledger.receiptReset": "Reset since this action",
  "ledger.receiptResetHelp": "This cell was reset or restored, or you are viewing a different cell. The original receipt is no longer in its ledger. A reused number will not open another action.",
  "ledger.actionRecorded": "Action recorded",
  "ledger.actionRecordedHelp": "This action committed a verified receipt without business events.",
  "ledger.emptyDescription":
    "Every committed business effect and definition install appends a hash-chained entry here.",
  "ledger.seq": "Seq",
  "ledger.commit": "Commit",
  "ledger.event": "Event",
  "ledger.subject": "Subject",
  "ledger.world": "World",
  "ledger.hash": "Hash",
  "ledger.hashLabel": "hash",
  "ledger.prevLabel": "prev",
  "ledger.range": "#{{first}}–#{{last}} of {{head}}",
  "ledger.afterPosition": "after #{{after}}",
  "ledger.back": "Back",
  "ledger.nextPage": "Next {{count}}",
  "ledger.latest": "Latest",
  "explorer.includeRegistryBaseMore_one": "Include registry base · {{count}} more",
  "explorer.includeRegistryBaseMore_other": "Include registry base · {{count}} more",
  "explorer.typeGraphCount_one": "{{count}} types · {{edgeCount}} edges",
  "explorer.typeGraphCount_other": "{{count}} types · {{edgeCount}} edges",
  "graph.moreProperties_one": "+{{count}} more",
  "graph.moreProperties_other": "+{{count}} more",
  "objects.deletedLinkTargets_one": "{{count}} link target(s) were deleted in their source; the links stay, flagged.",
  "objects.deletedLinkTargets_other": "{{count}} link target(s) were deleted in their source; the links stay, flagged.",
  "objects.upstreamFacts_one": "· {{count}} upstream fact",
  "objects.upstreamFacts_other": "· {{count}} upstream facts",
  "objects.failedCommands_one": "{{count}} failed command needs a decision.",
  "objects.failedCommands_other": "{{count}} failed commands need a decision.",
  "neighborhood.withheldLinks_one": "{{objectCount}} objects · {{count}} link not drawn (limit {{limit}} objects)",
  "neighborhood.withheldLinks_other": "{{objectCount}} objects · {{count}} links not drawn (limit {{limit}} objects)",
  "neighborhood.objectCount_one": "{{count}} objects",
  "neighborhood.objectCount_other": "{{count}} objects",
  "neighborhood.allLinksShown_one": "All links you may read are shown ({{count}}).",
  "neighborhood.allLinksShown_other": "All links you may read are shown ({{count}}).",

  "graph.source": "source",

  "permissions.conjunct.scope": "Scope",
  "permissions.conjunct.scopeChecks":
    "The request names this cell's org and environment, and the principal belongs to it.",
  "permissions.conjunct.capability": "<conjunction>and</conjunction>Capability",
  "permissions.conjunct.capabilityChecks": "The principal holds the exact scoped capability for this action.",
  "permissions.conjunct.delegation": "<conjunction>and</conjunction>Delegation",
  "permissions.conjunct.delegationChecks":
    "The principal acts directly, or through a verified personal automation approval.",
  "permissions.conjunct.management": "<conjunction>and</conjunction>Management",
  "permissions.conjunct.managementChecks": "Host-managed identity and delegation operations allow this change.",
  "permissions.conjunct.access": "<conjunction>and</conjunction>Access rules",
  "permissions.conjunct.accessChecks":
    "The admitted AccessIR rules, compiled to Cedar, permit this action on this resource.",
  "permissions.outcome.allow": "Allows",
  "permissions.outcome.deny": "Denies",
  "permissions.outcome.error": "Error, denies",
  "permissions.outcome.notEvaluated": "Not reached",
  "permissions.membership.role": "role",
  "permissions.membership.group": "group",
  "permissions.membership.businessUnit": "business unit",
  "permissions.membership.always": "always",
  "permissions.membership.until": "until {{date}}",
  "permissions.membership.from": "from {{date}}",
  "permissions.membership.validityRange": "{{from}} → {{until}}",
  "permissions.description":
    "Who can do what, and why. Every answer comes from the cell's own authorizer. The console only shows it and never decides anything itself.",
  "permissions.whoCanDoWhat": "Who can do what",
  "permissions.accessRules": "Access rules",
  "permissions.people": "People",
  "permissions.explain.question": "Permission question",
  "permissions.explain.principal": "Principal",
  "permissions.explain.otherPrincipal": "Explaining someone else needs norn.authz.explain.",
  "permissions.explain.selfPrincipal": "You, as the cell sees this connection.",
  "permissions.explain.may": "May",
  "permissions.explain.resourceType": "Resource type",
  "permissions.explain.resourceKey": "Resource key",
  "permissions.explain.keyPlaceholder": "key",
  "permissions.explain.actionInput": "Action input (optional)",
  "permissions.explain.notJson": "Not JSON: {{error}}",
  "permissions.explain.inputHelp": "Needed when the action's target or a membership restriction depends on its input.",
  "permissions.explain.noReadInput": "Reads take no input.",
  "permissions.explain.throughDelegation": "Through delegation (optional)",
  "permissions.explain.delegationIdPlaceholder": "delegation id",
  "permissions.explain.delegationHelp":
    "An agent explaining its own delegated read or action names the delegation here.",
  "permissions.explain.definitionsRefused":
    "Definitions could not be read ({{code}}), so actions and types are not suggested. Type them exactly; the cell still explains what you may do.",
  "permissions.explain.definitionsUnavailable":
    "Definitions could not be read, so actions and types are not suggested. Type them exactly; the cell still explains what you may do.",
  "permissions.explain.submit": "Explain",
  "permissions.explain.snapshotQuestion": "A snapshot question. Nothing runs and nothing changes.",
  "permissions.explain.thisQuestion": "this permission question",
  "permissions.explain.explanation": "Permission explanation",
  "permissions.explain.allowed": "Allowed",
  "permissions.explain.denied": "Denied",
  "permissions.explain.allowedSentence":
    "<principalValue>{{principal}}</principalValue> may <actionValue>{{action}}</actionValue> on <resourceValue>{{resource}}</resourceValue>.",
  "permissions.explain.deniedSentence":
    "<principalValue>{{principal}}</principalValue> may not <actionValue>{{action}}</actionValue> on <resourceValue>{{resource}}</resourceValue>.",
  "permissions.explain.stableReasonCode": "Stable reason code",
  "permissions.explain.checksOrder": "Checks, in the order the cell runs them",
  "permissions.explain.permittedBy": "Permitted by",
  "permissions.explain.determinedBy": "Determined by",
  "permissions.explain.hostPolicy": "A host policy, not from AccessIR",
  "permissions.explain.cedarPolicyId": "Cedar policy id",
  "permissions.explain.evaluationError": "An evaluation error forces a deny.",
  "permissions.explain.definitionEpoch": "Definitions v{{version}} · receipt <receiptValue>{{receipt}}</receiptValue>",
  "permissions.explain.permissionSnapshot": "Permission snapshot <epochValue>{{epoch}}</epochValue>",
  "permissions.explain.snapshotUnavailable": "Permission snapshot unavailable",
  "permissions.rules.installedRules": "the installed access rules",
  "permissions.rules.noneInstalled": "No access rules installed",
  "permissions.rules.installHelp":
    "Without AccessIR rules, Cedar permits nothing beyond host policies. Install a policy definition to grant access.",
  "permissions.rules.filterPlaceholder": "Filter by rule, action or type",
  "permissions.rules.filter": "Filter access rules",
  "permissions.rules.count_one": "{{shown}} of {{count}} rules",
  "permissions.rules.count_other": "{{shown}} of {{count}} rules",
  "permissions.rules.compiledCedarHelp":
    "Compiled Cedar appears beside each rule once the cell serves it. Norn compiles every rule when definitions are admitted; the console never compiles them itself, and Cedar is never edited here.",
  "permissions.rules.policy": "Policy {{id}}",
  "permissions.rules.when": "When <conditionValue>{{condition}}</conditionValue>",
  "permissions.rules.on": "on",
  "permissions.rules.compiledCedar": "Compiled Cedar · read-only",
  "permissions.rules.notServed": "Not served by the cell yet.",
  "permissions.people.connectionActsAs": "This connection acts as",
  "permissions.people.yourAccess": "Your access",
  "permissions.people.notPerson": "Not a person, so no memberships or delegations to show.",
  "permissions.people.connectionPrincipal": "who this connection acts as",
  "permissions.people.openPerson": "Open a person",
  "permissions.people.open": "Open",
  "permissions.people.readablePeople": "People you can read",
  "permissions.people.people": "people",
  "permissions.people.noneVisible": "No people are visible to you here.",
  "permissions.people.typeNotInstalled":
    "The installed definitions do not include {{objectType}}, so the people directory is unavailable. You can still open a person by key or view your own access.",
  "permissions.people.morePeople": "More people exist; open one by key.",
  "permissions.people.explainPerson": "Explain a permission for this person",
  "permissions.membership.memberships": "Memberships",
  "permissions.membership.namespaceHelp":
    "<desiredValue>desired</desiredValue> is what an administrator declared. <observedValue>observed</observedValue> comes from a source system and is read-only here.",
  "permissions.membership.personMemberships": "{{personId}}'s memberships",
  "permissions.membership.noneVisible":
    "No memberships visible to you. The cell shows only the memberships you may read, so this does not mean the person holds no roles.",
  "permissions.delegation.delegations": "Delegations",
  "permissions.delegation.granted": "Delegations granted",
  "permissions.delegation.help":
    "Each one lets an agent make one read and one action with this person's current permissions, until it expires or is revoked.",
  "permissions.delegation.personDelegations": "{{personId}}'s delegations",
  "permissions.delegation.noneGranted": "No delegations granted.",
  "permissions.membership.grants": "Grants",
  "permissions.membership.valid": "Valid",
  "permissions.membership.authority": "Authority",
  "permissions.membership.inEffectColumn": "In effect",
  "permissions.membership.outsideWindow": "outside its window now",
  "permissions.membership.inEffect": "in effect",
  "permissions.membership.notInEffect": "not in effect",
  "permissions.delegation.authoritySentence":
    "<executorValue>{{executor}}</executorValue> may read <readValue>{{read}}</readValue> and run <actionValue>{{action}}</actionValue> on <targetValue>{{target}}</targetValue>",
  "permissions.delegation.approvedRevoked":
    "Approved by <approverValue>{{approvedBy}}</approverValue> · revoked {{revokedAt}} by {{revokedBy}}",
  "permissions.delegation.approvedExpired":
    "Approved by <approverValue>{{approvedBy}}</approverValue> · expired {{expiresAt}}",
  "permissions.delegation.approvedExpires":
    "Approved by <approverValue>{{approvedBy}}</approverValue> · expires {{expiresAt}}",
  "permissions.delegation.revoke": "Revoke…",
  "permissions.delegation.confirmRevoking": "Confirm revoking {{id}}",
  "permissions.delegation.revokeQuestion": "Revoke this delegation?",
  "permissions.delegation.revokeConsequence":
    "<executorValue>{{executor}}</executorValue> loses this authority immediately. A revoked delegation cannot be restored; a new approval would be needed. Type <idValue>{{id}}</idValue> to confirm.",
  "permissions.delegation.confirmId": "Delegation id to confirm",
  "permissions.delegation.revokeDelegation": "Revoke delegation",
  "permissions.delegation.notFound":
    " The cell answers the same for a delegation that is gone and one you may not change. The list has been re-read.",
  "permissions.delegation.revocationUncertain":
    "The revocation may not have reached the cell ({{error}}). The list has been re-read; check its state before retrying.",
  "permissions.delegation.revokedAt": "Revoked at {{date}}.",

  "graph.nodeDescription": "Press enter or space to select a node. Press delete to remove it and escape to cancel.",
  "graph.nodeKeyboardDescription":
    "Press enter or space to select a node. You can then use the arrow keys to move the node around. Press delete to remove it and escape to cancel.",
  "graph.nodeMoved": "Moved selected node {{direction}}. New position, x: {{x}}, y: {{y}}",
  "graph.edgeDescription":
    "Press enter or space to select an edge. You can then press delete to remove it or escape to cancel.",
  "graph.controls": "Control Panel",
  "graph.zoomIn": "Zoom In",
  "graph.zoomOut": "Zoom Out",
  "graph.fitView": "Fit View",
  "graph.toggleInteractivity": "Toggle Interactivity",
  "graph.miniMap": "Mini Map",
  "graph.handle": "Handle",
  "graph.moveLeft": "left",
  "graph.moveRight": "right",
  "graph.moveUp": "up",
  "graph.moveDown": "down",

  "apps.description":
    "Pages declared by admitted widgets, rendered from their WidgetIR. Reads and actions go through the cell; conditions are Norn's to evaluate.",
  "apps.noneInstalled": "No apps installed",
  "apps.installHelp":
    "An app is a widget definition with a UX contract (pages, bindings, fields, forms). Install one with the definitions and it appears here.",
  "apps.pages_one": "{{count}} pages",
  "apps.pages_other": "{{count}} pages",
  "apps.thisApp": "this app",
  "apps.noSuchPage": "No such page in this app",
  "apps.noSuchApp": "No such app installed",
  "apps.notDeclared": "<widgetValue>{{id}}</widgetValue> is not declared by any admitted widget at set v{{version}}.",
  "apps.invalidParameters":
    "This page cannot open with these parameters: {{problems}}. Open it from a navigation in this app.",
  "decision.tableFallback": "table",
  "decision.registryReadOnly": "This decision is part of the registry base, which is read-only.",
  "decision.onlyRuleIr2": "Only RuleIR 2.0 decision graphs are editable here.",
  "decision.noDecisionTable": "This decision graph has no decision table.",
  "decision.onlyTextCells": "A cell holds a non-text value; this editor changes text ZEN cells only.",
  "decision.reshapedError":
    "Rows or columns of {{names}} changed. This editor saves cell text only; reset the table structure, then try again.",
  "decision.installFailedInTransit": "The install request to {{baseUrl}} failed in transit",
  "decision.setMoved": "The installed set moved to v{{version}}",
  "decision.artifactMissing": "{{artifactId}} is no longer in the tenant layer",
  "decision.notInstalledDecision": "Not an installed decision",
  "decision.notARule": "<artifactValue>{{artifactId}}</artifactValue> is not a rule in set v{{version}}.",
  "decision.checkDidNotRun": "Check did not run",
  "decision.nothingToInstall": "Nothing to install: no cell differs from the installed decision.",
  "decision.installUnknown":
    "The install request to {{baseUrl}} failed and the installed set could not be re-read, so it may or may not have been applied. Reload the definitions and compare before trying again.",
  "decision.notInstalled": "Not installed",
  "decision.installNotApplied":
    "A re-read shows set v{{version}} is still active. The request failed before the cell committed it, most often because the browser's CORS check refused it. Cells accept the x-norn-admin-token header only from exactly configured origins (#3196), so add {{origin}} to this cell's allowed origins, or open the console on an allowed origin.",
  "decision.notSent": "Not sent",
  "decision.narrowing": "Narrowing: {{details}}",
  "decision.table": "Decision table",
  "decision.noChanges": "No changes",
  "decision.changedCells_one": "{{count}} cell changed",
  "decision.changedCells_other": "{{count}} cells changed",
  "decision.check": "Check",
  "decision.installOpen": "Install…",
  "decision.installedSuccess":
    "Installed as set v{{version}}. The cell re-checked and committed it; the editor now shows the installed decision.",
  "decision.staleNow":
    "The installed set is now v{{version}} (you planned against v{{plannedVersion}}); it may include this install or someone else's. Your edits are still here; reload to see what is installed now.",
  "decision.staleRefused":
    "The cell refused the install: the set changed after v{{plannedVersion}}. Your edits are still here; reload to see what is installed now.",
  "decision.discardReload": "Discard edits and reload",
  "decision.reshapedNotice":
    "This editor saves cell text only. Rows or columns of {{names}} changed, so Check and Install are off until the structure is back.",
  "decision.resetStructure": "Reset table structure",
  "decision.readOnlyReason": "{{reason}} Shown read-only.",
  "decision.editor": "Decision table editor",
  "decision.tables": "Decision tables",
  "decision.editHelp":
    "Edit a cell to change its ZEN expression; row descriptions are editable too. Rows, columns and the hit policy are fixed here, so change those in the decision's source definition. A <dashValue>-</dashValue> under a column name means the column has no input field: each cell is a full expression over the decision input (<sliceValue>slice</sliceValue>, <eventValue>event</eventValue>).",
  "decision.candidateChecks": "Candidate and checks",
  "decision.installNewSet": "Install as a new definition set",
  "decision.installDescription_one":
    "Sends the whole tenant layer with {{count}} changed cell, planned against set v{{version}}. The cell re-checks it; the registry base is not sent and stays as selected.",
  "decision.installDescription_other":
    "Sends the whole tenant layer with {{count}} changed cells, planned against set v{{version}}. The cell re-checks it; the registry base is not sent and stays as selected.",
  "decision.adminCredential": "Admin install credential",
  "decision.credentialHelp": "Held in memory for this save only. Your identity also needs norn.defs.install.",
  "decision.install": "Install",
  "decision.checks": "Checks",
  "decision.tablePreviewHelp": "The table node as it will be sent. Layout fields stay untouched.",
  "decision.checkHelp": "Edit a cell, then check it with the same Rust checker the cell runs.",
  "decision.changedCheckHelp":
    "Check runs the Rust checker in this browser against the installed registry base. Install re-checks on the cell.",
  "decision.admissible": "Admissible",
  "decision.notAdmissible": "Not admissible",
  "decision.outcomeUnknown": "Outcome unknown — re-check before retrying",
  "decision.reloadDefinitions": "Reload definitions",

  "decision.invalidVersion": "definition set version is not a number",
  "decision.invalidDefinitionResponse": "GET /v1/defs did not return an object",
  "common.couldNotReach": "Could not reach {{url}}",
  "live.definitionsLive": "Definitions live · v{{revision}}",
  "live.connecting": "Connecting to definition changes…",
  "live.notLive": "Definitions not live",
  "live.noRead": "You can no longer read definitions here.",
  "live.closed": "The cell closed the definitions stream.",

  "decision.nothingToSave": "Nothing to save: no cell or node position differs from the installed decision.",
  "decision.movedNodes_one": "{{count}} node moved",
  "decision.movedNodes_other": "{{count}} nodes moved",
  "decision.saveLayoutOpen": "Save layout…",
  "decision.installedPendingLive": "Installed as set v{{version}}. Waiting for the cell's change notice to show it.",
  "decision.installedPendingOffline":
    "Installed as set v{{version}}. Live updates are off here ({{reason}}), so reload to show it.",
  "decision.liveConnecting": "connecting",
  "decision.reload": "Reload",
  "decision.refreshFailed":
    "The definitions could not be read again. The editor still shows v{{version}}; your edits are kept.",
  "decision.refreshFailedWithReason":
    "The definitions could not be read again ({{reason}}). The editor still shows v{{version}}; your edits are kept.",
  "decision.upstreamChanged":
    "This decision changed upstream in v{{version}}. Your edits are kept and still based on v{{basisVersion}}; installing them now would be refused.",
  "decision.upstreamRemoved":
    "This decision was removed upstream in v{{version}}. Your edits are kept and still based on v{{basisVersion}}; installing them now would be refused.",
  "decision.hideComparison": "Hide comparison",
  "decision.compare": "Compare",
  "decision.discardAndLoad": "Discard my edits and load v{{version}}",
  "decision.upstreamRebased":
    "The set moved to v{{version}}; other definitions changed, not this decision. Your edits will install on top of v{{version}}.",
  "decision.view": "Editor view",
  "decision.tableView": "Tables",
  "decision.canvasView": "Canvas",
  "decision.tableViewHelp": "Cell text is edited here.",
  "decision.canvasViewHelp":
    "Drag nodes to arrange the graph. Positions are saved as layout, never as part of the decision.",
  "decision.saveLayout": "Save the canvas layout",
  "decision.layoutDescription_one":
    "Saves {{count}} node position in the layout sidecar, planned against set v{{version}}. The decisions are sent exactly as installed, so their hashes and the checker receipt stay the same.",
  "decision.layoutDescription_other":
    "Saves {{count}} node positions in the layout sidecar, planned against set v{{version}}. The decisions are sent exactly as installed, so their hashes and the checker receipt stay the same.",
  "decision.installMovedCellDescription_one":
    "Sends the whole tenant layer with {{cells}} changed cell and {{count}} moved node, planned against set v{{version}}. The cell re-checks it; the registry base is not sent and stays as selected.",
  "decision.installMovedCellDescription_other":
    "Sends the whole tenant layer with {{cells}} changed cell and {{count}} moved nodes, planned against set v{{version}}. The cell re-checks it; the registry base is not sent and stays as selected.",
  "decision.installMovedCellsDescription_one":
    "Sends the whole tenant layer with {{cells}} changed cells and {{count}} moved node, planned against set v{{version}}. The cell re-checks it; the registry base is not sent and stays as selected.",
  "decision.installMovedCellsDescription_other":
    "Sends the whole tenant layer with {{cells}} changed cells and {{count}} moved nodes, planned against set v{{version}}. The cell re-checks it; the registry base is not sent and stays as selected.",
  "decision.tableRemoved": "table removed",
  "decision.tableStructureChanged": "rows or columns changed",
  "decision.upstreamComparison": "Upstream comparison",
  "decision.changedUpstream": "Changed upstream in v{{version}}",
  "decision.noChangedTableCell":
    "No table cell differs; the decision changed elsewhere (another node, metadata or its layout).",
  "decision.cellAlsoEdited": "you edited this cell too",
  "decision.canvasUndone":
    "Only node positions change here; that edit was undone. Wiring and nodes belong to the decision's source definition.",
  "common.dismiss": "Dismiss",
  "common.yes": "Yes",
  "common.no": "No",
  "ws.mode.label": "Mode",
  "ws.mode.workspace": "Business",
  "ws.mode.builder": "Data",
  "ws.nav.home": "Home",
  "ws.nav.areas": "Area map",
  "ws.nav.businessAreas": "Business areas",
  "ws.how.label": "How this works",
  "ws.how.home": "Metrics are DuckDB queries over an authorized snapshot of the cell; open a tile's lineage to see the head it was computed at.",
  "ws.how.areas": "Ontology types mapped to shared Core types. Areas come from installed apps; lines are typed links declared between their types.",
  "ws.how.list": "Generated from the type definition: columns, labels, money and status formats come from the ontology, not a hand-built screen.",
  "ws.how.record": "Typed links; every hop is permission-checked. Related records are read one authorized hop at a time.",
  "ws.how.why": "Provenance: source → observation → mapping → value. Each value cites the recorded action or source record that wrote it.",
  "ws.how.restricted": "Cedar field-level policy; Use ≠ Reveal. Hidden values still count and link, but are never shown to you.",
  "ws.how.builder": "Every change is an authorized action with a receipt. Definitions, ledger and permissions are the control plane behind the workspace.",
  "ws.hiddenByPolicy": "Hidden by policy",
  "ws.hiddenByPolicyHelp": "The cell withheld this value for your role.",
  "ws.hiddenByPolicyMarkings": "Withheld for your role · {{markings}}",
  "ws.titleHidden": "Name hidden by policy",
  "ws.home.title": "Today",
  "ws.home.signedInAs": "signed in as {{actor}}",
  "ws.home.searchHint": "Search anything",
  "ws.home.metrics": "Metrics",
  "ws.home.attention": "Open work",
  "ws.home.nothingOpen": "No records are waiting on a next step.",
  "ws.home.openCount_one": "{{count}}{{more}} open",
  "ws.home.openCount_other": "{{count}}{{more}} open",
  "ws.home.openMap": "Open map",
  "ws.home.recent": "Recently viewed",
  "ws.home.recentEmpty": "Records you open appear here.",
  "ws.home.howConnected": "How the business connects",
  "ws.home.asOfHead": "as of head {{head}}",
  "ws.home.lineage": "Lineage",
  "ws.home.metricPending": "Not computed yet.",
  "ws.home.metricUnavailable": "This metric is not readable here.",
  "ws.areas.title": "Area map",
  "ws.areas.description": "Each area is an installed app or a shared Core domain. Lines are typed links between their records.",
  "ws.areas.mapLabel": "Business areas and the links between them",
  "ws.areas.none": "No business areas are installed.",
  "ws.areas.noSuchArea": "This area is not part of the installed definitions.",
  "ws.areas.core": "Core",
  "ws.areas.coreHelp": "shared Core domain",
  "ws.areas.types_one": "{{count}} type",
  "ws.areas.types_other": "{{count}} types",
  "ws.areas.lifecycleStates_one": "{{count}} state",
  "ws.areas.lifecycleStates_other": "{{count}} states",
  "ws.areas.flowLabel": "How records flow in {{area}}",
  "ws.areas.connections": "Connections to other areas",
  "ws.areas.allTypes": "All types",
  "ws.list.record": "Record",
  "ws.list.total_one": "{{count}} record",
  "ws.list.total_other": "{{count}} records",
  "ws.list.loadedMore_one": "{{count}} loaded · more available",
  "ws.list.loadedMore_other": "{{count}} loaded · more available",
  "ws.list.importedFrom": "imported from {{source}}",
  "ws.list.filter": "Filter",
  "ws.list.filterPlaceholder": "Filter {{name}}…",
  "ws.list.byStatus": "Filter by status",
  "ws.list.empty": "No {{name}} visible to you",
  "ws.list.emptyHelp": "The cell returned no records you are allowed to read in this scope.",
  "ws.list.noMatch": "Nothing loaded matches this filter.",
  "ws.list.loadMore": "Load more",
  "ws.list.inspect": "Inspect in Builder",
  "ws.record.inspect": "Inspect in Builder",
  "ws.record.notVisible": "This record isn't visible to you",
  "ws.record.notVisibleHelp": "The cell does not show this {{type}} to your role, or it does not exist.",
  "ws.record.lifecycle": "Lifecycle",
  "ws.cmd.title": "Actions",
  "ws.cmd.checkedAtCommit": "A new record can only be authorized when it is committed; the cell checks policy then and shows any refusal.",
  "ws.cmd.checking": "Checking permission with the cell…",
  "ws.cmd.denied": "Not permitted for you: {{reason}}",
  "ws.cmd.unavailable": "Permission check refused: {{reason}}",
  "ws.cmd.readOnly": "{{actor}} can read this record but cannot run actions on it. {{reason}}",
  "ws.cmd.confirm": "Runs as {{actor}}. The cell checks policy again, then commits and records it in the ledger.",
  "ws.cmd.commit": "Commit",
  "ws.cmd.committing": "Committing…",
  "ws.cmd.committed": "Committed #{{seq}}",
  "ws.cmd.replayed": "Already committed; this is the original receipt.",
  "ws.cmd.refused": "The cell refused this action",
  "ws.cmd.receipt": "View receipt",
  "ws.cmd.chooseRecord": "Choose an existing record…",
  "ws.cmd.whyDenied": "Why can't I run “{{action}}”?",
  "ws.cmd.deniedHelp": "The cell checked your current permissions and did not grant this action on this record. The record has not changed.",
  "ws.cmd.deniedScope": "The request's organization or environment is missing or outside your current access. Check the selected scope. The record has not changed.",
  "ws.cmd.deniedCapability": "Your current access does not include this action in this organization and environment. Ask an administrator to review your access. The record has not changed.",
  "ws.cmd.deniedDelegation": "The cell did not accept the delegation for this action. Check its scope, approval and expiry. The record has not changed.",
  "ws.cmd.deniedManagement": "The cell's identity-management restrictions do not permit this action. Ask an administrator to review the restriction. The record has not changed.",
  "ws.cmd.deniedInvalidRequest": "The cell could not evaluate this permission request because its action, identity or input is invalid. Check the request details. The record has not changed.",
  "ws.cmd.deniedEvaluation": "The cell could not safely evaluate this permission request. Ask an administrator to review the permission check. The record has not changed.",
  "ws.cmd.deniedStaleAccess": "Your access changed while the cell checked this action. Refresh this page and try again. The record has not changed.",
  "ws.cmd.deniedTargetUnavailable": "The target record is unavailable to this action. Refresh the record before trying again. The record has not changed.",
  "ws.cmd.required": "Required",
  "ws.cmd.fixed": "From this record",
  "ws.cmd.invalidNumber": "Enter a number.",
  "ws.cmd.invalidJson": "Enter valid JSON.",
  "ws.record.moveTo": "Move to {{state}}",
  "ws.record.moreActions": "More",
  "ws.record.availableFrom": "from {{states}}",
  "ws.record.noApp": "No installed app runs this step.",
  "ws.record.openInApp": "Open in app",
  "ws.record.details": "Details",
  "ws.record.identifiers_one": "Identifiers and system fields · {{count}}",
  "ws.record.identifiers_other": "Identifiers and system fields · {{count}}",
  "ws.related.title": "Related",
  "ws.related.none": "No linked records.",
  "ws.related.hidden": "Links of this record are hidden for your role.",
  "ws.related.failed": "Links could not be read.",
  "ws.related.pointingHere": "These records link to this one.",
  "ws.related.showAll_one": "Show {{count}}",
  "ws.related.showAll_other": "Show all {{count}}",
  "ws.related.showFewer": "Show fewer",
  "ws.related.unreadable": "Not readable for your role",
  "ws.why.title": "Why it looks like this",
  "ws.why.fullLineage": "Full lineage",
  "ws.why.hidden": "The history of this record is hidden for your role.",
  "ws.why.createdBy": "Created by “{{action}}” · {{actor}} · {{when}}",
  "ws.why.truncated": "Earlier history not shown",
  "ws.why.noSource": "No recorded source",
  "ws.why.sourceDocument": "Source document",
  "ws.why.observed": "Observed",
  "ws.why.mapping": "Mapping",
  "ws.why.actionInput": "Action input",
  "ws.why.byActor": "by {{actor}}",
  "ws.why.committed": "Committed",
  "ws.why.thisField": "This field",
  "ws.why.chainFor": "Where {{field}} came from",
  "ws.search.open": "Search",
  "ws.search.title": "Search the workspace",
  "ws.search.placeholder": "Search areas, collections, records or a key…",
  "ws.search.go": "Go to",
  "ws.search.collections": "Collections",
  "ws.search.records": "Records",
  "ws.search.openKey": "Open “{{key}}”",
  "ws.search.nothing": "No matches. Try a collection name or an exact key.",
  "ws.why.unavailable": "The history of this record could not be read.",
  "ws.why.evidenceUnavailable": "Recorded source evidence could not be read.",
  "ws.home.listUnavailable": "{{name}} are not readable here.",
  "overview.displayGaps": "Display metadata gaps",
  "overview.displayGapsHelp_one": "{{count}} place where the workspace derived a label from an identifier. Declaring a display name or title property removes it.",
  "overview.displayGapsHelp_other": "{{count}} places where the workspace derived labels from identifiers. Declaring display names and title properties removes them.",
  "ws.why.allFields_one": "Show {{count}} field",
  "ws.why.allFields_other": "Show all {{count}} fields",
  "ws.related.withheld": "Links of this record are withheld because their keys carry markings you can't see.",
  "objects.writtenByAction": "Action input · {{action}} · by {{actor}} · {{commit}}",
  "ws.lineage.title": "Lineage",
  "ws.lineage.freshness": "Freshness",
  "ws.lineage.freshUntil": "Fresh until",
  "ws.lineage.sourceHead": "Source head",
  "ws.lineage.definition": "Definition",
  "ws.lineage.result": "Immutable result",
  "ws.lineage.openMetric": "Open metric and its Why",
  "ws.lineage.openResult": "Open the result snapshot",
  "dv.nav.data": "Data",
  "dv.nav.summary": "Summary",
  "dv.nav.analyze": "Analyze",
  "dv.nav.internals": "Internals",
  "dv.nav.ontology": "Ontology",
  "dv.nav.explore": "Explore",
  "dv.nav.metrics": "Metrics",
  "dv.nav.sources": "Sources",
  "dv.nav.policy": "Policy",
  "dv.how.home": "The same cell as Business, read by meaning: ontology, records, metrics, sources and policy. Every read is authorized for you.",
  "dv.how.ontology": "Types, properties and typed links from the admitted definitions, with their Core mappings and markings. Counts are authorized reads.",
  "dv.how.explore": "Start from a set of records and pivot along typed links. Joins use declared key properties; withheld keys never join.",
  "dv.how.metrics": "Metrics are DuckDB queries over an authorized snapshot. Each row shows its source head, definition and immutable result.",
  "dv.how.sources": "Provenance: source → observation → mapping → value. Receipts come from recorded evidence, never from guesses.",
  "dv.how.policy": "Cedar decides per field. What you see here is what the cell answered for you, not a role-based guess.",
  "dv.home.title": "Data workspace",
  "dv.home.description": "An analyst's view of the same business: the ontology behind the screens, records you can pivot across, metrics with lineage, where values came from, and what policy lets you see.",
  "dv.home.types": "Types",
  "dv.home.typesHint": "{{tenant}} tenant · {{core}} Core",
  "dv.home.properties": "Properties",
  "dv.home.links": "Links",
  "dv.home.bridges_one": "{{count}} cross-area link",
  "dv.home.bridges_other": "{{count}} cross-area links",
  "dv.home.sources": "Source systems, mappings and per-record observation receipts.",
  "dv.home.mappings_one": "{{count}} mapping",
  "dv.home.mappings_other": "{{count}} mappings",
  "dv.home.sourceSystems": "Source systems",
  "dv.home.policies": "Policies",
  "dv.home.version": "Definitions",
  "dv.home.ontology": "Every type with its properties, links, Core mappings, markings and record counts.",
  "dv.home.explore": "Search a type, then pivot: these customers → their orders → their payments.",
  "dv.home.graph": "Investigate one record's neighbourhood link by link, with a side inspector.",
  "dv.home.metrics": "Each metric with its definition, freshness, source head, immutable result and drill to rows.",
  "dv.home.policy": "What this principal can and cannot see, field by field, and why.",
  "dv.ontology.description": "The admitted ontology: types grouped by business area, with properties, typed links, Core mappings and markings.",
  "dv.ontology.index": "Types",
  "dv.ontology.filter": "Filter types or properties…",
  "dv.ontology.explore": "Explore records",
  "dv.ontology.businessList": "Business list",
  "dv.ontology.definition": "Definition",
  "dv.ontology.records": "Records",
  "dv.ontology.recordsHint": "readable by you",
  "dv.ontology.countRefused": "The cell did not count these records for you.",
  "dv.ontology.linksOut": "Links out",
  "dv.ontology.linksIn": "Links in",
  "dv.ontology.marked": "Marked fields",
  "dv.ontology.properties": "Properties",
  "dv.ontology.colLabel": "Label",
  "dv.ontology.colId": "Property",
  "dv.ontology.colType": "Type",
  "dv.ontology.colRole": "Reads as",
  "dv.ontology.colMarkings": "Markings",
  "dv.ontology.links": "Typed links",
  "dv.ontology.out": "out",
  "dv.ontology.in": "in",
  "dv.ontology.declaredBy": "declared by {{ns}}",
  "dv.ontology.noLinks": "No typed links.",
  "dv.ontology.core": "Core and source mappings",
  "dv.ontology.implements": "implements",
  "dv.ontology.mapsTo": "maps to",
  "dv.ontology.bridgedFrom": "bridged from",
  "dv.ontology.via": "via {{link}}",
  "dv.ontology.mapping": "mapping",
  "dv.ontology.sourceSchema": "source schema",
  "dv.ontology.noCore": "No Core interface, Core link or source mapping declared.",
  "dv.ontology.lifecycle": "Lifecycle",
  "dv.role.title": "Title",
  "dv.role.identifier": "Identifier",
  "dv.role.status": "Status",
  "dv.role.money": "Money",
  "dv.role.currency": "Currency",
  "dv.role.date": "Date",
  "dv.role.quantity": "Quantity",
  "dv.role.reference": "Reference",
  "dv.role.contact": "Contact",
  "dv.role.flag": "Flag",
  "dv.role.text": "Text",
  "dv.role.technical": "System",
  "dv.explore.description": "Search records of one type, then pivot along typed links to the records they connect to.",
  "dv.explore.start": "Start from",
  "dv.explore.chain": "Pivot path",
  "dv.explore.removeStep": "Remove last pivot",
  "dv.explore.filter": "Filter this set…",
  "dv.explore.pivot": "Pivot",
  "dv.explore.refused": "The cell did not return this set for you.",
  "dv.explore.noKey": "This link declares no key property, so it can only be followed record by record. Open a record's graph instead.",
  "dv.explore.empty": "No readable records in this set.",
  "dv.explore.emptyPartial": "Only the records loaded so far were searched.",
  "dv.explore.loadMore": "Load more records",
  "dv.explore.loading": "Loading…",
  "dv.explore.partialRoot": "Showing the {{shown}} records loaded so far; search and pivots use these.",
  "dv.explore.partial": "Showing the first {{shown}} records; the scan covers at most 2,000 per type.",
  "dv.explore.note": "Pivots join on declared key properties over authorized reads; masked keys never join.",
  "dv.metrics.description": "Every installed metric: its value, freshness, the source head it was computed at, its definition and its immutable result.",
  "dv.metrics.none": "No metrics are installed in this scope.",
  "dv.metrics.drill": "Explore {{name}}",
  "dv.metrics.row": "Row",
  "dv.metrics.value": "Value",
  "dv.metrics.groups_one": "{{count}} group",
  "dv.metrics.groups_other": "{{count}} groups",
  "dv.sources.description": "Where values come from: connected source systems, their mappings into the ontology, and the receipts behind each record.",
  "dv.sources.systems": "Source systems",
  "dv.sources.noSystems": "No source schema or connector is installed in this scope.",
  "dv.sources.connector": "Connector",
  "dv.sources.schemaShape": "{{datasets}} datasets · {{fields}} fields",
  "dv.sources.mappings": "Mappings",
  "dv.sources.noMappings": "No mappings are installed in this scope.",
  "dv.sources.lookup": "Receipts for a record",
  "dv.sources.mappedTag": "mapped",
  "dv.sources.keyPlaceholder": "Record key",
  "dv.sources.show": "Show",
  "dv.sources.noReceipts": "No source evidence is available for this version. It may have been written by a recorded action; open its Why to see.",
  "dv.sources.openWhy": "Open its Why",
  "dv.receipt.title": "Source receipts",
  "dv.receipt.from": "From",
  "dv.receipt.via": "via",
  "dv.receipt.observed": "observed {{when}}",
  "dv.receipt.recorded": "recorded {{when}}",
  "dv.receipt.commit": "commit #{{commit}}",
  "dv.receipt.fields": "Supplied: {{fields}}",
  "dv.policy.description": "What this principal can and cannot see, by type and field, as the cell answered it.",
  "dv.policy.principalUnavailable": "The cell did not resolve the principal.",
  "dv.policy.principal": "Principal",
  "dv.policy.roles": "Roles",
  "dv.policy.units": "Business units",
  "dv.policy.policies": "Access policies",
  "dv.policy.explain": "Reads are authorized per record and per field. Marked fields stay usable in links and metrics but are withheld from you unless your policy reveals them (Use ≠ Reveal).",
  "dv.policy.type": "Type",
  "dv.policy.access": "Your access",
  "dv.policy.withheld": "Withheld from you",
  "dv.policy.declared": "Declared markings",
  "dv.policy.refused": "Not readable",
  "dv.policy.read": "Readable",
  "dv.policy.readMasked": "Readable, partly masked",
  "dv.policy.none": "No records visible",
  "dv.policy.more": "Rules and explanations live in",
  "dv.source.tab": "Source",
  "dv.source.imported": "Imported from",
  "dv.source.authored": "Authored in",
  "dv.source.generated": "Generated by",
  "dv.source.recorded": "Recorded origin",
  "dv.source.members": "Source fields → Norn properties",
  "dv.source.sourceField": "Source field",
  "dv.source.nornProperty": "Norn property",
  "dv.source.compiled": "Compiled IR",
  "dv.sources.someMasked": "Some fields are withheld from you, so their evidence may be too.",
  "dv.policy.accessSampled": "Your access (one sampled record)",
  // Workbooks (#3379)
  "dv.home.workbooks": "Sheets and notebook cells over transforms: match two systems, review the pairs, promote the rule to a sync.",
  "wb.nav": "Workbooks",
  "wb.title": "Shopify ↔ ERPNext orders",
  "wb.description": "Do the store and the books agree? Each cell reads live records from this cell, the match pairs them, and every verdict you give is recorded as an authorized action with a receipt.",
  "wb.how": "A workbook is a page of cells read top to bottom: sources, a transform, a match, a review queue and a chart. Each cell's output is a live sheet, and its lineage shows where every column came from.",
  "wb.list.title": "Workbooks",
  "wb.list.description": "Spreadsheet-style notebooks over the transforms that keep your systems in sync.",
  "wb.list.orders": "Match the store's orders to ERPNext Sales Orders, review what disagrees, and promote the rule to a sync.",
  "wb.list.cells": "Sources · Transform · Match · Review · Chart",
  "wb.list.notInstalled": "This cell has no Shopify order feed yet.",
  "wb.unknown": "There is no workbook with this name.",
  "wb.notInstalled": "No Shopify order feed in this cell",
  "wb.notInstalledBody": "The workbook needs the shopify.Order and commerce.SalesOrder types. Install the order-match definitions to use it.",
  "wb.run": "Run",
  "wb.readRefused": "This sheet could not be read",
  "wb.hidden": "Hidden by policy",
  "wb.noReference": "None recorded",
  "wb.kind.source": "Source",
  "wb.kind.transform": "Transform",
  "wb.kind.match": "Match",
  "wb.kind.review": "Review",
  "wb.kind.chart": "Chart",
  "wb.cell.shopify": "Shopify orders",
  "wb.cell.erp": "ERPNext Sales Orders",
  "wb.cell.normalize": "Read Shopify's text as numbers and days",
  "wb.cell.match": "Match orders",
  "wb.cell.review": "Review the pairs",
  "wb.cell.chart": "Unmatched by week",
  "wb.cell.expand": "Expand cell",
  "wb.cell.collapse": "Collapse cell",
  "wb.lineage.toggle": "Lineage",
  "wb.lineage.chain": "Where this cell's rows come from",
  "wb.lineage.column": "Column",
  "wb.lineage.from": "Comes from",
  "wb.lineage.means": "What a value means",
  "wb.sheet.filter": "Filter rows",
  "wb.sheet.filtered": "{{shown}} of {{total}} rows",
  "wb.sheet.noMatch": "No row matches this filter.",
  "wb.col.order": "Order",
  "wb.col.placed": "Placed",
  "wb.col.total": "Total",
  "wb.col.currency": "Currency",
  "wb.col.status": "Status",
  "wb.col.customer": "Customer",
  "wb.col.reference": "ERP reference",
  "wb.col.date": "Date",
  "wb.col.amount": "Amount",
  "wb.col.day": "Day",
  "wb.col.shopifyText": "As Shopify sent it",
  "wb.col.verdict": "Verdict",
  "wb.col.confidence": "Confidence",
  "wb.col.delta": "Δ total",
  "wb.col.days": "Days apart",
  "wb.col.why": "Why",
  "wb.side.shopify": "Shopify",
  "wb.side.erp": "ERPNext",
  "wb.side.noShopify": "The store has no order for this.",
  "wb.side.noErp": "ERPNext has no order for this.",
  "wb.empty.shopify": "No Shopify orders have arrived yet.",
  "wb.empty.erp": "No ERPNext Sales Orders have arrived yet.",
  "wb.empty.match": "No pairs with this verdict.",
  "wb.empty.chart": "Nothing to chart yet.",
  "wb.stat.shopify": "Shopify orders",
  "wb.stat.erp": "ERPNext orders in window",
  "wb.stat.review": "Need review",
  "wb.stat.unmatched": "Disagree",
  "wb.stat.read": "Read",
  "wb.stat.live": "Live from this cell",
  "wb.normalize.allRead": "Every total and time read",
  "wb.normalize.body": "Shopify sends money and times as text (\"3600.00\", \"2026-09-14T21:40:00+03:00\"). This cell reads each total as a number and each time as the calendar day the customer saw in the store's own time zone. A row it cannot read is marked red and never matched by amount.",
  "wb.match.summary": "{{pairs}} pairs · {{review}} to review",
  "wb.match.filter": "Show pairs by verdict",
  "wb.match.all": "All",
  "wb.rule.reference": "Reference",
  "wb.rule.referenceBody": "The store order carries the ERPNext order its sync recorded. Same total: matched. Different total: amount mismatch. A second store order on it: duplicate.",
  "wb.rule.amountDate": "Customer + amount + date",
  "wb.rule.amountDateBody": "No reference: pair with an unclaimed ERPNext order of the same total within ±{{days}} days, preferring the same customer. A person confirms every such pair.",
  "wb.rule.days": "Days",
  "wb.rule.amount": "Amount",
  "wb.rule.leftover": "Leftovers",
  "wb.rule.leftoverBody": "A store order with no pair is missing in ERPNext; an ERPNext order in the store's dates with no pair is missing in Shopify.",
  "wb.rules.exact": "exact",
  "wb.rules.text": "Reference first; then amount ({{amount}}) and date within ±{{days}} days, confirmed by a person; leftovers are missing on one side.",
  "wb.reason.matched": "Matched",
  "wb.reason.fuzzy": "Matched · confirm",
  "wb.reason.amount_mismatch": "Amount mismatch",
  "wb.reason.missing_in_erp": "Missing in ERP",
  "wb.reason.missing_in_shopify": "Missing in Shopify",
  "wb.reason.duplicate": "Duplicate",
  "wb.why.reference": "{{shop}} names {{erp}} as its ERPNext order, and both totals agree.",
  "wb.why.amountSameDay": "{{shop}} has no ERPNext reference; {{erp}} is the only order with the same total on the same day.",
  "wb.why.amountDayApart": "{{shop}} has no ERPNext reference; {{erp}} has the same total a day apart, likely a late-evening order booked the next day.",
  "wb.why.amountMismatch": "{{shop}} and {{erp}} are the same order, but Shopify's total is {{delta}} {{direction}}: often shipping or a discount booked on one side only.",
  "wb.why.more": "more",
  "wb.why.less": "less",
  "wb.why.duplicate": "{{shop}} is a second store order pointing at {{erp}}; ERPNext booked the sale once.",
  "wb.why.missingInErp": "{{shop}} has no ERPNext reference and no ERPNext order has its total within the date tolerance: the sale never reached the books.",
  "wb.why.unknownReference": "{{shop}} names {{erp}}, which ERPNext has not sent to this cell.",
  "wb.why.missingInShopify": "{{erp}} falls inside the store's dates but no store order matches it: a phone or in-store sale, or a lost web order.",
  "wb.review.keys": "Keys:",
  "wb.review.accept": "accept",
  "wb.review.reject": "reject",
  "wb.review.showDecided": "Show decided",
  "wb.review.empty": "Nothing left to review. Every pair the rules could not settle has a verdict.",
  "wb.review.queue": "Pairs to review",
  "wb.review.accepted": "Accepted",
  "wb.review.rejected": "Rejected",
  "wb.review.acceptPair": "Accept pair",
  "wb.review.rejectPair": "Reject",
  "wb.review.recordedAccepted": "Accepted · receipt #{{seq}}",
  "wb.review.recordedRejected": "Rejected · receipt #{{seq}}",
  "wb.review.replayed": "already recorded",
  "wb.review.wasAccepted": "Accepted earlier",
  "wb.review.wasRejected": "Rejected earlier",
  "wb.review.ledger": "Open the ledger",
  "wb.review.refused": "The cell did not record this verdict",
  "wb.review.alternatives": "Other ERPNext orders with the same total and date: {{orders}}",
  "wb.review.summary": "{{open}} open · {{decided}} decided",
  "wb.review.receipt": "Receipt",
  "wb.chart.summary": "{{weeks}} weeks",
  "wb.chart.week": "Week",
  "wb.mean.shopOrder": "The order name staff and customers see in Shopify.",
  "wb.mean.placed": "When the customer placed the order, in the store's time zone.",
  "wb.mean.shopTotal": "What the customer was charged, including shipping and tax, as Shopify's text.",
  "wb.mean.financial": "Whether the customer has paid (paid) or not yet (pending).",
  "wb.mean.email": "The customer's email; contact details are hidden unless your role may see them.",
  "wb.mean.reference": "The ERPNext Sales Order the store's order sync recorded on this order, if it did.",
  "wb.mean.erpOrder": "The Sales Order number in ERPNext.",
  "wb.mean.erpDate": "The day ERPNext booked the order.",
  "wb.mean.erpTotal": "The order's grand total in ERPNext.",
  "wb.mean.erpStatus": "Draft, submitted and so on, from ERPNext's document status.",
  "wb.mean.amount": "Shopify's total read as a number; empty when the text is not plain decimal.",
  "wb.mean.day": "The calendar day the customer saw, used for the date tolerance.",
  "wb.mean.verdict": "Which rule paired the orders, and whether the two systems agree.",
  "wb.mean.confidence": "How sure the rule is, 0–100: a reference with equal totals is 100 (80 when it names another customer); an amount + date pair is at most 85, 95 with the same customer, 40 with a different one.",
  "wb.mean.delta": "How much more (or less) Shopify charged than ERPNext booked.",
  "wb.mean.days": "Days between the store's day and ERPNext's order date.",
  "wb.mean.erpCustomer": "The customer the order is booked to in ERPNext, by name; hidden unless your role may see contact details.",
  "wb.mean.customerSignal": "Whether the store order's email is the email of the customer ERPNext booked the order to (letter case ignored). Unknown when either email is hidden or missing.",
  "wb.col.email": "Email",
  "wb.customer.same": "Same customer",
  "wb.customer.different": "Different customer",
  "wb.customer.unknown": "Customer unknown",
  "wb.why.customerSame": "Both sides name the same customer (same email).",
  "wb.why.customerDifferent": "But the store's customer ({{shop}}) is not ERPNext's ({{erp}}).",
  "wb.signal.customer": "Customer",
  "wb.signal.amount": "Amount gap",
  "wb.signal.days": "Days apart",
  "wb.signal.same": "none",
  "wb.mean.decision": "Your verdict, recorded by the cell as an authorized action.",
  "wb.mean.receipt": "The ledger position of that action: the proof it happened.",
  "wb.mean.week": "The Monday of each week; bars count pairs that are not clean matches.",
  "wb.promote.button": "Promote to sync",
  "wb.promote.title": "Promote this match to a sync",
  "wb.promote.description": "The reference rule becomes a running definition: every order the store sends gets its ERPNext pair, and orders without one wait here for review.",
  "wb.promote.generated": "Generated by the Norn TypeScript SDK from",
  "wb.promote.maps": "{{system}} {{dataset}} → {{target}}",
  "wb.promote.check": "Checker",
  "wb.promote.checking": "Checking against this cell's definitions…",
  "wb.promote.checkFailed": "The check did not run",
  "wb.promote.admitted": "Admitted against this cell's installed definitions.",
  "wb.promote.refused": "Refused by the checker:",
  "wb.promote.records": "Promoted with {{matched}} matched, {{review}} to review and {{unmatched}} disagreeing.",
  "wb.promote.install": "Promoting records the sync and its exact definition hash. Installing the mapping into the cell is an administrator's definitions change; until then Sources › Sync health lists it as proposed.",
  "wb.promote.confirm": "Promote",
  "wb.promote.pending": "Recording…",
  "wb.promote.done": "Done",
  "wb.promote.recorded": "Recorded · receipt #{{seq}}.",
  "wb.promote.seeSources": "See it under Sources",
  "wb.promote.notRecorded": "The cell did not record the promotion",
  "wb.health.title": "Sync health",
  "wb.health.live": "Live",
  "wb.health.proposed": "Proposed",
  "wb.health.admitted": "Checker admitted",
  "wb.health.notAdmitted": "Checker refused",
  "wb.health.now": "Now: {{matched}} matched · {{review}} to review · {{unmatched}} disagree",
  "wb.health.open": "Workbook",
  "wb.health.none": "No sync has been promoted yet.",
  "wb.health.openWorkbook": "Open the order-match workbook",
  "wb.needs.body": "{{missing}} store orders never reached ERPNext · {{unmatched}} pairs disagree",
  "wb.needs.open": "Review",
  "wb.sheet.rows_one": "{{count}} row",
  "wb.sheet.rows_other": "{{count}} rows",
  "wb.sheet.showAll_one": "Show {{count}} row",
  "wb.sheet.showAll_other": "Show all {{count}} rows",
  "wb.stat.outside_one": "{{count}} more outside the store's dates",
  "wb.stat.outside_other": "{{count}} more outside the store's dates",
  "wb.stat.decided_one": "{{count}} decided",
  "wb.stat.decided_other": "{{count}} decided",
  "wb.normalize.unreadable_one": "{{count}} row could not be read",
  "wb.normalize.unreadable_other": "{{count}} rows could not be read",
  "wb.needs.title_one": "{{count}} Shopify order pair needs you",
  "wb.needs.title_other": "{{count}} Shopify order pairs need you",
  "wb.why.ambiguous_one": "{{shop}} has no ERPNext reference; {{erp}} fits, but so does {{count}} other order: choose carefully.",
  "wb.why.ambiguous_other": "{{shop}} has no ERPNext reference; {{erp}} fits, but so do {{count}} other orders: choose carefully.",
  "wb.incomplete.truncated": "A source has more rows than the workbook reads at once (10,000), so the match is paused rather than reporting false gaps.",
  "wb.incomplete.refused": "A source or the decisions could not be read, so the match is paused rather than reporting false gaps.",
  "wb.kind.promote": "Promote",
  "pw.title": "Payments → ledger",
  "pw.description": "Do Paymob, Kashier and the bank agree with the books? Each cell reads the providers' own exports and the ERPNext ledger from this cell; the match explains every disagreement in plain words, and every verdict is recorded as an authorized action with a receipt.",
  "pw.list.body": "Reconcile Paymob and Kashier settlements and the bank statement to ERPNext invoices, Payment Entries and fee journals, then promote the postings as a proposed sync.",
  "pw.list.cells": "Sources · Transform · Match · Review · Charts · Promote",
  "pw.list.notInstalled": "This cell has no payment provider files yet.",
  "pw.notInstalled": "No payment provider files in this cell",
  "pw.notInstalledBody": "The workbook needs the paymob.Transaction, kashier.PayoutLine and ledger.SalesInvoice types. Install the payments definitions to use it.",
  "pw.stat.transactions": "Provider transactions",
  "pw.stat.providers": "Paymob {{paymob}} · Kashier {{kashier}}",
  "pw.stat.posted": "Posted",
  "pw.stat.toPost": "To post",
  "pw.stat.decided": "{{n}} decided",
  "pw.stat.unreconciled": "Unreconciled",
  "pw.stat.fees": "Fees + VAT",
  "pw.stat.feesHint": "EGP, both providers",
  "pw.lens.financeOnly": "Finance only",
  "pw.lens.note": "Fees, VAT and payout nets are visible to Finance only; your role sees amounts and matches, and the fee checks wait for Finance.",
  "pw.lens.chartHidden": "Provider fees are hidden for your role. The Finance lens shows them by provider and week.",
  "pw.cell.paymob": "Paymob transactions export",
  "pw.cell.settlements": "Paymob settlement report",
  "pw.cell.kashier": "Kashier payout report",
  "pw.cell.bank": "Bank statement",
  "pw.cell.ledger": "ERPNext ledger",
  "pw.cell.normalize": "One Payment shape for both providers",
  "pw.cell.matchTxns": "Transactions ↔ invoices and Payment Entries",
  "pw.cell.matchBatches": "Payout batches ↔ bank deposits",
  "pw.cell.review": "Review the findings",
  "pw.cell.feesChart": "Fees by provider by week",
  "pw.cell.unreconciledChart": "Unreconciled amount over time",
  "pw.cell.promote": "Proposed postings",
  "pw.ledger.summary": "{{invoices}} invoices · {{payments}} Payment Entries · {{journals}} journals",
  "pw.ledger.invoices": "Sales Invoices",
  "pw.ledger.payments": "Payment Entries",
  "pw.ledger.journals": "Journal Entries",
  "pw.col.transaction": "Transaction",
  "pw.col.type": "Type",
  "pw.col.storeOrder": "Store order",
  "pw.col.instrument": "Card or wallet",
  "pw.col.batch": "Payout batch",
  "pw.col.provider": "Provider",
  "pw.col.gross": "Gross",
  "pw.col.fee": "Fee",
  "pw.col.vat": "VAT",
  "pw.col.net": "Net",
  "pw.col.erp": "ERPNext document",
  "pw.col.delta": "Δ",
  "pw.col.lines": "Σ lines",
  "pw.col.reported": "Paid out",
  "pw.col.deposit": "Bank line",
  "pw.col.feeJournal": "Fee journal",
  "pw.col.proposal": "Would create",
  "pw.kind.capture": "Payment",
  "pw.kind.refund": "Refund",
  "pw.kind.chargeback": "Chargeback",
  "pw.empty.source": "No rows have arrived yet.",
  "pw.normalize.body": "Paymob exports piastres (\"150050\" is EGP 1,500.50) and Cairo wall-clock times; Kashier exports signed EGP and UTC instants. This cell reads both as signed EGP (refunds and chargebacks negative) on the Cairo calendar day, and keeps each provider's fee, VAT and payout batch beside them.",
  "pw.normalize.unreadable": "{{n}} rows could not be read",
  "pw.match.summary": "{{n}} transactions · {{review}} to review · {{toPost}} to post",
  "pw.batches.summary": "{{n}} batches · {{review}} to review",
  "pw.filter.review": "Needs a person",
  "pw.rule.posted": "Posted",
  "pw.rule.postedBody": "A submitted Payment Entry whose reference_no is the provider's transaction id: the capture is in the books.",
  "pw.rule.invoice": "Invoice",
  "pw.rule.invoiceBody": "Otherwise the store order leads to its ERPNext Sales Order and invoice: still owed → to post; already paid → a person checks; none → no invoice.",
  "pw.rule.fee": "Fee",
  "pw.rule.feeBody": "Each capture's fee must be the contract: Paymob {{paymob}}, Kashier {{kashier}}, plus 14% VAT on the fee. A second capture of the same order and amount is a duplicate.",
  "pw.rule.refund": "Refunds",
  "pw.rule.refundBody": "A refund is booked when its invoice has a credit note for the amount; a chargeback always needs a person.",
  "pw.rule.sum": "Sum",
  "pw.rule.sumBody": "The batch's transactions, net of fees, must add up to what the provider says it paid.",
  "pw.rule.deposit": "Deposit",
  "pw.rule.depositBody": "The bank line with the batch's bank reference is its deposit; failing that, one with the same amount within two days, for a person to confirm.",
  "pw.rule.missing": "Missing",
  "pw.rule.missingBody": "A batch the provider reports as transferred with no bank line never arrived.",
  "pw.rules.text": "Provider transaction id = Payment Entry reference_no; else the store order's ERPNext invoice; fees per contract (Paymob {{paymob}}, Kashier {{kashier}}, 14% VAT); payout batches to bank lines by reference, else amount within two days.",
  "pw.reason.posted": "Posted",
  "pw.reason.to_post": "To post",
  "pw.reason.refund_booked": "Refund booked",
  "pw.reason.fee_mismatch": "Fee mismatch",
  "pw.reason.duplicate_capture": "Duplicate capture",
  "pw.reason.no_invoice": "No invoice",
  "pw.reason.already_paid": "Already paid",
  "pw.reason.refund_no_credit_note": "Refund, no credit note",
  "pw.reason.chargeback": "Chargeback",
  "pw.reason.deposited": "Deposited",
  "pw.reason.deposit_by_amount": "Deposit by amount",
  "pw.reason.batch_not_sum": "Batch does not sum",
  "pw.reason.missing_payout": "Missing payout",
  "pw.reason.deposit_mismatch": "Deposit differs",
  "pw.why.posted": "{{provider}} {{id}} is in the books as {{erp}}.",
  "pw.why.postedCheck": "{{erp}} records {{provider}} {{id}}, but its amount, mode of payment or invoice does not agree with the transaction: check it is the right entry.",
  "pw.why.toPost": "{{id}} paid store order {{order}}; its invoice {{erp}} is still owed, so the sync proposes the Payment Entry.",
  "pw.why.toPostDelta": "{{id}} paid store order {{order}}; its invoice {{erp}} still owes a different amount ({{delta}} apart), often shipping charged online only.",
  "pw.why.refundBooked": "Refund {{id}} of EGP {{amount}} is booked as credit note {{erp}}.",
  "pw.why.feeMismatch": "{{provider}} charged {{charged}} in fees and VAT on {{id}}, but the contract says {{expected}}: {{delta}} too much.",
  "pw.why.duplicateCapture": "{{id}} captured store order {{order}} a second time (EGP {{amount}}, first capture {{first}}): the customer was charged twice.",
  "pw.why.noInvoice": "{{id}} paid store order {{order}}, but ERPNext has no submitted invoice for it.",
  "pw.why.noReference": "{{id}} paid store order {{order}}, which carries no ERPNext reference yet: settle it in the Shopify ↔ ERPNext orders workbook, then its invoice can be found.",
  "pw.why.noStoreOrder": "{{id}} names store order {{order}}, which never reached ERPNext.",
  "pw.why.alreadyPaid": "{{id}} would pay {{erp}}, but ERPNext already shows it paid: a second payment, or one booked through another rail.",
  "pw.why.refundNoCreditNote": "{{id}} refunded EGP {{amount}} on store order {{order}}, but invoice {{erp}} has no credit note: revenue is still overstated.",
  "pw.why.chargeback": "The card holder disputed store order {{order}}: {{id}} took back EGP {{amount}} and a chargeback fee.",
  "pw.why.deposited": "{{provider}} batch {{batch}} arrived as bank line {{ref}} on {{date}}.",
  "pw.why.depositByAmount": "Batch {{batch}} has no bank line with its reference {{expected}}; {{ref}} has the same amount within two days.",
  "pw.why.batchNotSum": "{{provider}} says batch {{batch}} paid out {{reported}}, but its transactions net to {{lines}}: {{delta}} unexplained.",
  "pw.why.missingPayout": "{{provider}} reports batch {{batch}} transferred on {{date}} (reference {{ref}}), but no bank line shows it.",
  "pw.why.depositMismatch": "Bank line {{ref}} for batch {{batch}} differs from what the provider reports by {{delta}}.",
  "pw.review.accept": "Accept",
  "pw.review.queue": "Findings to review",
  "pw.review.empty": "Nothing left to review. Every finding the rules could not settle has a verdict.",
  "pw.side.bank": "Bank",
  "pw.side.noDeposit": "No bank line shows this payout.",
  "pw.detail.contract": "contract {{fee}} + {{vat}}",
  "pw.detail.owed": "owed",
  "pw.detail.first": "First capture",
  "pw.chart.cumulative": "Cumulative",
  "pw.unreconciled.summary": "EGP {{amount}} open",
  "pw.promote.summary": "{{entries}} Payment Entries · {{journals}} fee journals",
  "pw.promote.body": "Promoting records two SDK-generated syncs, one per provider. Installed, each would propose these Payment Entries (into the provider's clearing account, reference_no the transaction) and one fee Journal Entry per payout batch (fee and VAT against clearing). They stay proposals: nothing is written to ERPNext.",
  "pw.promote.none": "Nothing waits to be posted.",
  "pw.promote.title": "Promote the payments match to syncs",
  "pw.promote.description": "Each provider's rules become a running definition that proposes the ERPNext Payment Entries and fee Journal Entries its files imply; findings keep waiting here for a person.",
  "pw.promote.install": "Promoting records each sync and its exact definition hash as a proposal. Installing a mapping is an administrator's definitions change, and no posting reaches ERPNext without approval.",
  "pw.mean.paymobId": "Paymob's transaction id: the reference a Payment Entry records.",
  "pw.mean.kashierId": "Kashier's transaction id: the reference a Payment Entry records.",
  "pw.mean.type": "A payment, a refund or a chargeback.",
  "pw.mean.storeOrder": "The store order the checkout paid (Shopify's order id).",
  "pw.mean.amountCents": "The amount in piastres, as Paymob exports it.",
  "pw.mean.grossEgp": "The amount in EGP, signed: refunds are negative.",
  "pw.mean.instrument": "The card brand and last digits, or the wallet; visible to Finance only.",
  "pw.mean.fee": "The provider's fee and the VAT on it; visible to Finance only.",
  "pw.mean.vat": "14% VAT on the provider's fee; visible to Finance only.",
  "pw.mean.settlement": "The settlement that paid this out, once one has.",
  "pw.mean.payout": "The payout this row belongs to.",
  "pw.mean.reportedNet": "What the provider says it paid into the bank; visible to Finance only.",
  "pw.mean.bankReference": "The bank's reference for the transfer.",
  "pw.mean.bankLine": "The bank's own reference for the line.",
  "pw.mean.credit": "Money in, EGP; visible to Finance only.",
  "pw.mean.invoice": "Invoices and credit notes, with the Sales Order each bills.",
  "pw.mean.paymentEntry": "Payments the books hold, with the provider transaction each records.",
  "pw.mean.journal": "Fee journals and bank deposits, tied to their payout by cheque_no.",
  "pw.mean.gross": "What the customer paid (or got back), EGP.",
  "pw.mean.day": "The Cairo calendar day of the movement.",
  "pw.mean.batch": "The settlement or payout that carries the movement to the bank.",
  "pw.mean.verdict": "Which rule settled the transaction, or why a person must.",
  "pw.mean.confidence": "How sure the rule is, 0–100: a posting found by reference is 100; a deposit found only by amount 65.",
  "pw.mean.erp": "The Payment Entry, invoice or credit note the verdict rests on.",
  "pw.mean.delta": "Gross minus what the ledger holds; for a fee mismatch, charged minus contract.",
  "pw.mean.lines": "The batch's transactions net of fees, added up; needs Finance's view.",
  "pw.mean.deposit": "The bank line that carried the payout.",
  "pw.mean.feeJournal": "The fee Journal Entry booked for the batch, if any.",
  "pw.mean.feesChart": "Each provider's fees and VAT per week; Finance only.",
  "pw.mean.unreconciledChart": "Gross not yet in the books per week: waiting to be posted, or waiting for a person; the line is the running total.",
  "pw.mean.proposedEntry": "One Payment Entry per successful capture.",
  "pw.mean.proposedJournal": "One fee journal line per settlement or payout row.",
  "lens.hidden": "Hidden",
  "lens.hiddenForRole": "Hidden for your role",
  "lens.whyHidden": "Why can't I see this?",
  "lens.markedAs": "This field is marked {{label}}.",
  "lens.revealedBy": "The {{policy}} policy names {{roles}} for it, in rule",
  "lens.anyRole": "Any role",
  "lens.noReveal": "No installed policy reveals this marking to anyone.",
  "lens.whoGrants": "An administrator who manages these policies can grant a named role; the role also needs the reveal capability, and the cell decides every read.",
  "lens.serverSide": "The cell withheld the value before it reached this browser; this note reads the installed policy.",
  "lens.viewingAs": "Viewing as {{lens}}",
  "lens.switch": "Switch department lens",
  "lens.viewAs": "View as",
  "ac.queue.title": "Needs you",
  "ac.queue.description": "Work waiting on someone, across apps, from the records themselves.",
  "ac.queue.overdue_one": "{{count}} overdue invoice",
  "ac.queue.overdue_other": "{{count}} overdue invoices",
  "ac.queue.unmatched_one": "{{count}} unmatched shop order",
  "ac.queue.unmatched_other": "{{count}} unmatched shop orders",
  "ac.queue.lowStock_one": "{{count}} item below reorder level",
  "ac.queue.lowStock_other": "{{count}} items below reorder level",
  "ac.queue.approval_one": "{{count}} sales order awaiting approval",
  "ac.queue.approval_other": "{{count}} sales orders awaiting approval",
  "ac.queue.approvalDetail": "Submitted or on hold · approval needed",
  "ac.queue.overdueDetail": "{{key}} · was due {{due}}",
  "ac.queue.unmatchedDetail": "Not matched to an ERPNext sales order",
  "ac.queue.lowStockDetail": "{{have}} on hand · reorder at {{floor}}",
  "ac.queue.openIn": "Open in {{app}}",
  "ac.queue.more": "{{count}} more",
  "ac.app.sell": "Sell",
  "ac.app.sellHint": "Point of sale, invoices and shop orders",
  "ac.app.cash": "Cash Ops",
  "ac.app.cashHint": "Collections, accounts and payouts",
  "ac.app.crm": "CRM",
  "ac.app.crmHint": "Customers, channels and occasions",
  "ac.app.studio": "Studio",
  "ac.app.studioHint": "Product shoots and generation jobs",
  "ac.app.inventory": "Inventory",
  "ac.app.inventoryHint": "Stock levels and reorder points",
  "ac.apps.title": "Apps",
  "ac.kpi.grossMargin": "Gross margin",
  "ac.kpi.commission": "Sales commission",
  "ac.kpi.overdue": "Overdue invoices",
  "ac.kpi.unmatched": "Unmatched shop orders",
  "ac.kpi.usesHidden": "The cell used {{label}} on {{type}} for this; the figure stays hidden for your role",
  "ac.kpi.fromRecords": "Summed by the cell from {{type}}",
  "ac.kpi.overdueSub_one": "{{count}}{{more}} waiting on collection",
  "ac.kpi.overdueSub_other": "{{count}}{{more}} waiting on collection",
  "ac.kpi.unmatchedSub_one": "{{count}}{{more}} needs a sales order",
  "ac.kpi.unmatchedSub_other": "{{count}}{{more}} need a sales order",
  "ac.kpi.lowStockSub_one": "{{count}}{{more}} to reorder",
  "ac.kpi.lowStockSub_other": "{{count}}{{more}} to reorder",
  "ac.kpi.approvalSub_one": "{{count}}{{more}} awaiting approval",
  "ac.kpi.approvalSub_other": "{{count}}{{more}} awaiting approval",
  "ac.sync.title": "Sync health",
  "ac.sync.records_one": "{{value}} record",
  "ac.sync.records_other": "{{value}} records",
  "ac.sync.rejected": "{{value}} rejected",
  "connections.title": "Connection health",
  "connections.refresh": "Refresh",
  "connections.description": "Read-only sources. Sync starts only after the required credentials and consent are in place.",
  "connections.empty": "No connections are configured for this scope.",
  "connections.unavailable": "Connection health could not be read.",
  "connections.protocolError": "The server returned an unsupported connection health response.",
  "connections.readOnly": "Read-only · no changes to the source",
  "connections.missing": "Waiting for credentials",
  "connections.lastSuccess": "Last successful sync",
  "connections.never": "No successful sync yet",
  "connections.records": "Records reported",
  "connections.retry": "Next retry",
  "connections.noRetry": "Not scheduled",
  "connections.failure": "Last sync issue:",
  "connections.gmailSetup": "Gmail setup details",
  "connections.callback": "OAuth callback URL",
  "connections.connecting": "Opening consent…",
  "connections.connectGmail": "Connect Gmail",
  "connections.status.waiting_for_credential": "Waiting for credentials",
  "connections.status.awaiting_consent": "Waiting for consent",
  "connections.status.ready": "Ready to sync",
  "connections.status.syncing": "Syncing",
  "connections.status.healthy": "Up to date",
  "connections.status.error": "Needs attention",
  "connections.status.disabled": "Disabled",
} as const

export type MessageKey = keyof typeof en
